import {
  SOCKET_CLOSE, SOCKET_PING_INTERVAL_MS,
  type AttachmentDTO, type ConversationDTO, type GifPage, type GroupInfo, type MessageDTO, type MessageReactionDTO, type MessagePage, type MessagingEvent, type MessagingServerFrame,
} from '@campus/core';

/**
 * Client de la messagerie (tasks/messagerie-plan.md) : API `/v1/messaging` et
 * WebSocket. Volontairement sans React ni Phaser, pour pouvoir devenir un
 * paquet partagé avec l'app mobile (`packages/messaging-client`).
 */

export interface MessagingAuth {
  apiBase: string;
  /** JWT courant (en mémoire), null si déconnecté. */
  token: () => string | null;
  /** Identifiant (ou slug) du tenant, en-tête X-Tenant-ID. */
  tenant: () => string;
}

export class MessagingApiError extends Error {
  constructor(public readonly code: string, public readonly status: number) { super(code); }
}

export class MessagingApi {
  constructor(private readonly auth: MessagingAuth) {}

  private async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${this.auth.apiBase}/v1/messaging${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${this.auth.token() ?? ''}`,
          'X-Tenant-ID': this.auth.tenant(),
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : null,
      });
    } catch {
      throw new MessagingApiError('NETWORK', 0);
    }
    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string };
      throw new MessagingApiError(data.error ?? `HTTP_${res.status}`, res.status);
    }
    return res.json() as Promise<T>;
  }

  conversations() { return this.call<ConversationDTO[]>('GET', '/conversations'); }
  unread() { return this.call<{ unread: number }>('GET', '/unread'); }
  openDirect(userId: string) { return this.call<{ id: string }>('POST', '/conversations/direct', { userId }); }

  // Groupes
  createGroup(title: string, memberIds: string[]) { return this.call<GroupInfo>('POST', '/conversations', { title, memberIds }); }
  renameGroup(id: string, title: string) { return this.call<GroupInfo>('PATCH', `/conversations/${encodeURIComponent(id)}`, { title }); }
  addMembers(id: string, userIds: string[]) { return this.call<GroupInfo>('POST', `/conversations/${encodeURIComponent(id)}/members`, { userIds }); }
  setAdmin(id: string, userId: string, admin: boolean) {
    return this.call<GroupInfo>('PATCH', `/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`, { admin });
  }
  removeMember(id: string, userId: string) {
    return this.call<GroupInfo>('DELETE', `/conversations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`);
  }
  deleteGroup(id: string) { return this.call<{ status: 'ok' }>('DELETE', `/conversations/${encodeURIComponent(id)}`); }
  leaveGroup(id: string) { return this.call<{ status: 'ok' }>('POST', `/conversations/${encodeURIComponent(id)}/leave`); }

  messages(conversationId: string, opts: { before?: string; after?: string; around?: string; limit?: number } = {}) {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(opts)) if (v !== undefined) q.set(k, String(v));
    const qs = q.toString();
    return this.call<MessagePage>('GET', `/conversations/${encodeURIComponent(conversationId)}/messages${qs ? `?${qs}` : ''}`);
  }

  changes(conversationId: string, since: string) {
    return this.call<MessageDTO[]>('GET', `/conversations/${encodeURIComponent(conversationId)}/changes?since=${encodeURIComponent(since)}`);
  }

  send(conversationId: string, body: { text: string; clientId: string; senderName?: string; attachmentIds?: string[]; replyToId?: string }) {
    return this.call<MessageDTO>('POST', `/conversations/${encodeURIComponent(conversationId)}/messages`, body);
  }

  read(conversationId: string, messageId?: string) {
    return this.call<{ lastReadAt: string }>('POST', `/conversations/${encodeURIComponent(conversationId)}/read`, messageId ? { messageId } : {});
  }

  edit(messageId: string, text: string) { return this.call<MessageDTO>('PATCH', `/messages/${encodeURIComponent(messageId)}`, { text }); }
  remove(messageId: string) { return this.call<MessageDTO>('DELETE', `/messages/${encodeURIComponent(messageId)}`); }
  /** Ajoute (`on`) ou retire sa réaction ; renvoie toutes les réactions du message. */
  react(messageId: string, reactionId: string, on: boolean) {
    return this.call<{ conversationId: string; reactions: MessageReactionDTO[] }>(on ? 'PUT' : 'DELETE',
      `/messages/${encodeURIComponent(messageId)}/reactions/${encodeURIComponent(reactionId)}`);
  }

  /**
   * Envoie un fichier (corps brut, nom dans `X-File-Name`). XMLHttpRequest
   * plutôt que fetch : seul lui donne la progression de l'envoi.
   */
  /** `voice` : message vocal enregistré (le serveur le convertit en M4A, 5 min au plus). */
  upload(conversationId: string, file: Blob, name: string, opts: { onProgress?: (ratio: number) => void; signal?: AbortSignal; voice?: boolean } = {}): Promise<AttachmentDTO> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${this.auth.apiBase}/v1/messaging/conversations/${encodeURIComponent(conversationId)}/attachments${opts.voice ? '?voice=1' : ''}`);
      xhr.setRequestHeader('Authorization', `Bearer ${this.auth.token() ?? ''}`);
      xhr.setRequestHeader('X-Tenant-ID', this.auth.tenant());
      xhr.setRequestHeader('X-File-Name', encodeURIComponent(name));
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.upload.onprogress = (e) => { if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total); };
      xhr.onload = () => {
        let data: unknown = null;
        try { data = JSON.parse(xhr.responseText); } catch { /* corps vide */ }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data as AttachmentDTO);
        else reject(new MessagingApiError((data as { error?: string } | null)?.error ?? `HTTP_${xhr.status}`, xhr.status));
      };
      xhr.onerror = () => reject(new MessagingApiError('NETWORK', 0));
      xhr.onabort = () => reject(new MessagingApiError('ABORTED', 0));
      opts.signal?.addEventListener('abort', () => xhr.abort());
      xhr.send(file);
    });
  }

  // GIF (Giphy, par l'API)
  gifStatus() { return this.call<{ enabled: boolean }>('GET', '/gifs/status'); }
  searchGifs(query: string, offset = 0) {
    const q = new URLSearchParams({ q: query, offset: String(offset) });
    return this.call<GifPage>('GET', `/gifs?${q}`);
  }
  /** Adresse absolue d'un aperçu (relative à l'API dans la réponse de recherche). */
  gifPreviewUrl(previewUrl: string) { return `${this.auth.apiBase}${previewUrl}`; }
  /** Range le GIF comme un fichier joint de la conversation. */
  attachGif(conversationId: string, gifId: string) {
    return this.call<AttachmentDTO>('POST', `/conversations/${encodeURIComponent(conversationId)}/gifs`, { gifId });
  }

  /** Fichier ou aperçu en blob (images : le jeton passe en en-tête). */
  async blob(attachmentId: string, variant: 'original' | 'preview'): Promise<Blob> {
    let res: Response;
    try {
      res = await fetch(`${this.auth.apiBase}/v1/messaging/attachments/${encodeURIComponent(attachmentId)}${variant === 'preview' ? '/preview' : ''}`, {
        headers: { Authorization: `Bearer ${this.auth.token() ?? ''}`, 'X-Tenant-ID': this.auth.tenant() },
      });
    } catch {
      throw new MessagingApiError('NETWORK', 0);
    }
    if (!res.ok) throw new MessagingApiError(`HTTP_${res.status}`, res.status);
    return res.blob();
  }

  /** Adresse signée à mettre dans `src` (vidéo, son) ou à ouvrir (document). */
  /** `download` : servi en pièce jointe (enregistré, jamais ouvert à la place de la page). */
  async fileUrl(attachmentId: string, download = false): Promise<{ url: string; expiresAt: string }> {
    const r = await this.call<{ url: string; external: boolean; expiresAt: string }>('POST', `/attachments/${encodeURIComponent(attachmentId)}/url`, download ? { download: true } : {});
    return { url: r.external ? r.url : `${this.auth.apiBase}${r.url}`, expiresAt: r.expiresAt };
  }
}

export type SocketStatus = 'idle' | 'connecting' | 'online' | 'offline' | 'closed';

const RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 15_000];

/**
 * WebSocket de la messagerie : authentification par le premier message,
 * PING applicatif (le navigateur ne montre pas les pings de protocole),
 * reconnexion 1, 2, 4, 8 puis 15 s (+ aléa), immédiate au retour du réseau.
 * Jamais de reconnexion après un refus d'authentification ou une révocation.
 */
export class MessagingSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private stopped = true;
  private lastHeardAt = 0;
  private readonly onOnline = () => { if (!this.stopped && !this.ws) this.connectNow(); };
  // Retour sur l'onglet : les minuteries ont pu être ralenties (Chrome, 1/min) ;
  // délai de grâce et ping tout de suite plutôt qu'un faux verdict de coupure.
  private readonly onVisible = () => {
    if (document.visibilityState !== 'visible' || this.stopped) return;
    this.lastHeardAt = Date.now();
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'ping' }));
    else if (!this.ws) this.connectNow();
  };

  constructor(
    private readonly auth: MessagingAuth,
    private readonly handlers: {
      onEvent: (event: MessagingEvent) => void;
      /** `ready` à chaque connexion réussie (la première comprise) : le moment de rattraper. */
      onReady: () => void;
      onStatus: (status: SocketStatus) => void;
    },
  ) {}

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisible);
    this.connectNow();
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisible);
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.clearPing();
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000);
    this.handlers.onStatus('idle');
  }

  /** « En train d'écrire » (privé seulement côté serveur). */
  typing(conversationId: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'typing', conversationId }));
  }

  private connectNow(): void {
    if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
    const token = this.auth.token();
    if (!token) { this.handlers.onStatus('closed'); return; }
    this.handlers.onStatus('connecting');
    const url = `${this.auth.apiBase.replace(/^http/, 'ws')}/v1/messaging/socket`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token }));
    ws.onmessage = (e) => {
      this.lastHeardAt = Date.now();
      let frame: MessagingServerFrame;
      try { frame = JSON.parse(String(e.data)) as MessagingServerFrame; } catch { return; }
      if (frame.type === 'ready') {
        this.attempt = 0;
        this.handlers.onStatus('online');
        this.startPing(ws);
        this.handlers.onReady();
        return;
      }
      if (frame.type === 'pong' || frame.type === 'error') return;
      this.handlers.onEvent(frame);
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return; // remplacée ou arrêtée
      if (e.code === SOCKET_CLOSE.AUTH_FAILED || e.code === SOCKET_CLOSE.REVOKED) {
        this.ws = null;
        this.clearPing();
        this.handlers.onStatus('closed');
        return;
      }
      this.retryLater();
    };
  }

  private retryLater() {
    const ws = this.ws;
    this.ws = null;
    this.clearPing();
    if (ws) { ws.onclose = null; ws.onmessage = null; try { ws.close(); } catch { /* */ } }
    if (this.stopped) return;
    this.handlers.onStatus('offline');
    const base = RETRY_DELAYS_MS[Math.min(this.attempt, RETRY_DELAYS_MS.length - 1)]!;
    this.attempt += 1;
    this.retryTimer = setTimeout(() => this.connectNow(), base + Math.random() * 500);
  }

  private startPing(ws: WebSocket) {
    this.clearPing();
    this.lastHeardAt = Date.now();
    this.pingTimer = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;
      // Connexion muette (veille, réseau coupé net) : la fermeture n'arrive
      // parfois jamais. Pas de verdict onglet masqué (minuteries ralenties).
      if (document.visibilityState === 'visible' && Date.now() - this.lastHeardAt > SOCKET_PING_INTERVAL_MS * 2 + 5_000) {
        this.retryLater();
        return;
      }
      ws.send(JSON.stringify({ type: 'ping' }));
    }, SOCKET_PING_INTERVAL_MS);
  }

  private clearPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }
}
